import React from 'react';
import { Box, Container, Body, Grid, GridItem, Heading, Link } from '@iris/react-components';
import {
  ChecklistIcon,
  ClockIcon,
  DocumentIcon,
  MoneyIcon,
  ShieldCheckIcon,
} from '@iris/react-icons';
import { sendClick } from 'reaxl-analytics';
import { SMALL_FEE_HASH } from '@/constants/landingPageAnchors';
import ChangeStateFooter from './ChangeStateFooter';

type LocationData = {
  zip?: string;
  state?: string;
  stateName?: string;
  city?: string;
  county?: string;
};

type BenefitIcon = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false'; role?: string }>;
type BenefitId = 'skip-dmv' | 'paperwork-facilitation' | 'clean-title' | 'secure-payment' | 'fraud-protection';

type BenefitItem = {
  id: BenefitId;
  title: string;
  description: string;
  icon: BenefitIcon;
};

type BenefitFlow = 'dealer' | 'escrow';

type BenefitDescriptions =
  | { default: string }
  | { dealer: string; escrow: string };

const NOOP_ASYNC = (): Promise<void> => Promise.resolve();

const BENEFIT_META: Record<BenefitId, { title: string; icon: BenefitIcon }> = {
  'skip-dmv': {
    title: 'Skip the DMV',
    icon: ClockIcon,
  },
  'paperwork-facilitation': {
    title: 'Paperwork Facilitation',
    icon: DocumentIcon,
  },
  'clean-title': {
    title: 'Clean Title',
    icon: ChecklistIcon,
  },
  'secure-payment': {
    title: 'Secure Online Payment',
    icon: MoneyIcon,
  },
  'fraud-protection': {
    title: 'Fraud Protection',
    icon: ShieldCheckIcon,
  },
};

const BENEFIT_ORDER_BY_FLOW: Record<BenefitFlow, BenefitId[]> = {
  dealer: ['skip-dmv', 'clean-title', 'secure-payment', 'fraud-protection'],
  escrow: ['paperwork-facilitation', 'secure-payment', 'clean-title', 'fraud-protection'],
};

const BENEFIT_DESCRIPTION_BY_ID: Record<BenefitId, BenefitDescriptions> = {
  'skip-dmv': {
    default: 'Save time by skipping the DMV**. Choosing our Full Service checkout option means we handle all the paperwork for your newly purchased vehicle, including tax, title, and registration. All you need to do is print your temporary permit to drive the car.',
  },
  'paperwork-facilitation': {
    default: 'Choosing our Full Service checkout option means we help you fill out all the paperwork you need to register and title the car, which you\'ll then bring to the DMV to finalize the sale.',
  },
  'clean-title': {
    dealer: 'You receive a clean title that is verified to be transferrable into your name when you choose our Full Service checkout option. If you are financing the purchase, we will record the lienholder on the title as part of the transfer process.',
    escrow: 'You\'ll receive a clean title that is verified to be transferrable into your name when you choose our Full Service checkout option. If you are financing the purchase, we will record the lienholder on the title as part of the transfer process.',
  },
  'secure-payment': {
    dealer: 'Pay for your car, tax, and registration fees online into an escrow account. We securely hold funds until you\'ve test-driven and inspected the vehicle (optional), releasing payment to the seller only after you\'ve officially taken possession.',
    escrow: 'Pay for your car online into an escrow account. We securely hold funds until you\'ve test-driven and inspected the vehicle (optional), releasing payment to the seller only after you\'ve officially taken possession.',
  },
  'fraud-protection': {
    default: 'We verify the seller\'s identity and confirm the vehicle is registered to them before the sale is completed. Each vehicle also includes a complimentary AutoCheck history report.',
  },
};

function getBenefitDescription(id: BenefitId, flow: BenefitFlow): string {
  const descriptions = BENEFIT_DESCRIPTION_BY_ID[id];

  if ('default' in descriptions) {
    return descriptions.default;
  }

  return descriptions[flow];
}

function getBenefits(isDealerEligible: boolean): BenefitItem[] {
  const flow: BenefitFlow = isDealerEligible ? 'dealer' : 'escrow';

  return BENEFIT_ORDER_BY_FLOW[flow].map((id) => ({
    id,
    title: BENEFIT_META[id].title,
    icon: BENEFIT_META[id].icon,
    description: getBenefitDescription(id, flow),
  }));
}

interface BenefitsOfBuyingSectionProps {
  isDealerEligible?: boolean;
  locationData?: LocationData;
  onZipChange?: (zip: string) => Promise<void>;
}

export default function BenefitsOfBuyingSection({
  isDealerEligible = false,
  locationData,
  onZipChange,
}: BenefitsOfBuyingSectionProps) {
  const benefits = getBenefits(isDealerEligible);

  // Pixall Event C2. Small Fee Link
  const onSmallFeeLabelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const pixallData = {
      hasListingType: false,
      label: 'text-link::small-fee',
      eventSource: 'link',
      pageId: 'slp_psx_buy',
      cdl: {
        event: {
          action: 'Click',
          element: 'Small Fee Link',
          tagIndex: 'psx-121',
        },
      },
    };
    sendClick('sycClickEvent', event, {
      isPixallEvent: true,
      pixallData,
    });

    // The link's href hash matches the fees section anchor. When the hash is
    // already set (e.g. clicking again after collapsing the accordion),
    // the browser won't fire 'hashchange' on its own, so dispatch one
    // manually to re-trigger FeesAndTaxesSection's accordion auto-open.
    if (window.location.hash === SMALL_FEE_HASH) {
      window.dispatchEvent(new Event('hashchange'));
    }
  };

  return (
    <Box
      className="benefits-of-buying"
      aria-labelledby="benefits-title"
      data-testid="benefits-of-buying-section"
    >
      <Container maxWidth="none" className="benefits-container">
          <Box className="title-wrapper" data-testid="benefits-title-wrapper">
            <Heading as="h2" size="xl" className="title" id="benefits-title" data-testid="benefits-title">
              Benefits of Buying Through Our Car Marketplace
            </Heading>
            <Body as="p" size="lg" className="subtitle" data-testid="benefits-subtitle">
              Here are the available benefits that Autotrader Private Seller Exchange offers for a
              {' '}
              {/* Figma: no focus ring on anchor link */}
              <Link variant="inline" href={SMALL_FEE_HASH} className="highlight iris:focus:outline-none iris:focus:ring-0 iris:active:outline-none" data-testid="benefits-small-fee-link" onClick={onSmallFeeLabelClick}>small fee</Link>
              {' '}
              based on your registration state*.
            </Body>
          </Box>

          <Grid
            columns={{ xs: 1, md: 2 }}
            gap="md"
            className="benefits-row"
            data-testid="benefits-row"
          >
            {benefits.map((benefit) => {
              const IconComponent = benefit.icon;
              return (
                <GridItem
                  key={benefit.id}
                  className="iris:p-0"
                  data-testid={`benefit-card-${benefit.id}`}
                >
                  <Box className="benefit-card">
                    <Box className="benefit-card-header">
                      <Box className="benefit-card-icon">
                        {/* Decorative icon; adjacent Heading conveys the label. */}
                        <IconComponent
                          className="benefit-icon"
                          aria-hidden
                          role="presentation"
                        />
                      </Box>
                      <Heading as="h3" size="sm" className="benefit-card-title">{benefit.title}</Heading>
                    </Box>
                    <Body as="p" size="md" className="benefit-description">{benefit.description}</Body>
                  </Box>
                </GridItem>
              );
            })}
          </Grid>

          <Box className="footer-wrapper" data-testid="benefits-footer-wrapper">
            <ChangeStateFooter
              text="*These are the services provided if you register the vehicle in"
              selectedState={locationData?.stateName || ''}
              currentZip={locationData?.zip || ''}
              onSave={onZipChange ?? NOOP_ASYNC}
              modalText="Autotrader Private Seller Exchange services differ based on what state you are registering the car. Enter the ZIP code where you are registering your vehicle."
            />
            {isDealerEligible && (
              <Body as="p" size="sm" className="seller-action-note" data-testid="seller-action-note">
                **Seller action (or cooperation) required (or other limitations as is applicable)
              </Body>
            )}
          </Box>
      </Container>
    </Box>
  );
}
